import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import APIClient from '../api/axios';
import { toast } from 'react-toastify';
import {
  FiDollarSign, FiAlertCircle, FiShoppingCart,
  FiUsers, FiBarChart2, FiFolder, FiTrendingUp,
  FiCheckCircle, FiDownload, FiRefreshCw, FiCalendar,
  FiChevronRight, FiPieChart, FiX, FiActivity,
} from 'react-icons/fi';
import './Reports.css';

/* ── helpers ── */
const fmt    = n => '₹' + Number(n || 0).toLocaleString('en-IN');
const pct    = (a, b) => (b ? ((a / b) * 100).toFixed(1) : '0.0');
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_COLOR = {
  planning: '#64748b', in_progress: '#2563eb', review: '#f59e0b',
  completed: '#16a34a', on_hold: '#dc2626',
};
const STATUS_LABEL = {
  planning: 'Planning', in_progress: 'In Progress', review: 'Review',
  completed: 'Completed', on_hold: 'On Hold',
};
const PO_STATUS_COLOR = { pending: '#f59e0b', sent: '#2563eb', partial: '#7c3aed', received: '#16a34a' };

/* ── Donut chart (SVG) ── */
function DonutChart({ slices, size = 120, hole = 0.62 }) {
  const r = size / 2;
  const rMid = r * (1 - (1 - hole) / 2);
  const strokeW = r * (1 - hole);
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  if (!total) return (
    <svg width={size} height={size}>
      <circle cx={r} cy={r} r={rMid} fill="none" stroke="#e2e8f0" strokeWidth={strokeW} />
    </svg>
  );
  let angle = -90;
  const arcs = slices.filter(sl => sl.value > 0).map((sl, i) => {
    const sweep = (sl.value / total) * 360;
    const a1 = (angle * Math.PI) / 180;
    const a2 = ((angle + sweep) * Math.PI) / 180;
    const x1 = r + rMid * Math.cos(a1), y1 = r + rMid * Math.sin(a1);
    const x2 = r + rMid * Math.cos(a2), y2 = r + rMid * Math.sin(a2);
    const large = sweep > 180 ? 1 : 0;
    angle += sweep;
    return <path key={i} d={`M ${x1} ${y1} A ${rMid} ${rMid} 0 ${large} 1 ${x2} ${y2}`}
      fill="none" stroke={sl.color} strokeWidth={strokeW} strokeLinecap="butt" />;
  });
  return <svg width={size} height={size}>{arcs}</svg>;
}

/* ── Mini bar chart (SVG) ── */
function BarChart({ bars, height = 80 }) {
  const max = Math.max(...bars.map(b => b.value), 1);
  const w = 100 / bars.length;
  return (
    <div className="rpt-bar-wrap">
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
        {bars.map((b, i) => {
          const bh = Math.max((b.value / max) * (height - 14), 1);
          return <rect key={i} x={i * w + w * 0.15} y={height - bh - 5}
            width={w * 0.7} height={bh} rx="2" fill={b.color || '#2563eb'} opacity="0.85" />;
        })}
      </svg>
      <div className="rpt-bar-labels">
        {bars.map((b, i) => <span key={i}>{b.label}</span>)}
      </div>
    </div>
  );
}

/* ── Report nav tabs ── */
const REPORTS = [
  { id: 'overview',           icon: <FiBarChart2 />,   color: '#2563eb', bg: '#eff6ff', label: 'Overview' },
  { id: 'project-summary',   icon: <FiFolder />,      color: '#7c3aed', bg: '#f5f3ff', label: 'Projects' },
  { id: 'payment-summary',   icon: <FiDollarSign />,  color: '#16a34a', bg: '#f0fdf4', label: 'Payments' },
  { id: 'pending-collections',icon: <FiAlertCircle />, color: '#f59e0b', bg: '#fffbeb', label: 'Pending' },
  { id: 'purchase-summary',  icon: <FiShoppingCart />, color: '#0891b2', bg: '#ecfeff', label: 'Purchases' },
  { id: 'vendor-report',     icon: <FiUsers />,       color: '#dc2626', bg: '#fef2f2', label: 'Vendors' },
  { id: 'customer-report',   icon: <FiUsers />,       color: '#059669', bg: '#ecfdf5', label: 'Customers' },
];

export default function Reports() {
  const [loading, setLoading]     = useState(true);
  const [data, setData]           = useState(null);
  const [activeReport, setActive] = useState('overview');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [search, setSearch]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, clientRes, finRes, poRes, vendorRes] = await Promise.all([
        APIClient.get('/projects'),
        APIClient.get('/clients'),
        APIClient.get('/finance/projects'),
        APIClient.get('/purchase/orders').catch(() => ({ data: [] })),
        APIClient.get('/purchase/vendors').catch(() => ({ data: [] })),
      ]);
      setData({
        projects:    projRes.data   || [],
        clients:     clientRes.data || [],
        finProjects: finRes.data    || [],
        orders:      poRes.data     || [],
        vendors:     vendorRes.data || [],
      });
    } catch {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── date filter ── */
  const inRange = useCallback((dateStr) => {
    if (!dateRange.from && !dateRange.to) return true;
    const d = new Date(dateStr);
    if (isNaN(d)) return true;
    if (dateRange.from && d < new Date(dateRange.from)) return false;
    if (dateRange.to   && d > new Date(dateRange.to + 'T23:59:59')) return false;
    return true;
  }, [dateRange]);

  /* ── derived metrics ── */
  const metrics = useMemo(() => {
    if (!data) return null;
    const { projects, clients, finProjects, orders, vendors } = data;

    const byStatus = projects.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});
    const totalBudget = finProjects.reduce((s, p) => s + (+p.budget || 0), 0);
    const totalReceived = finProjects.reduce((s, p) => s + (+p.received || 0), 0);
    const totalPending  = finProjects.reduce((s, p) => s + Math.max(0, p.pending || 0), 0);
    const totalPO       = orders.reduce((s, o) => s + (+o.totalAmount || 0), 0);
    const receivedPO    = orders.filter(o => o.status === 'received').reduce((s, o) => s + (+o.totalAmount || 0), 0);

    /* month bars from finProjects (no payment date on them, use createdAt) */
    const monthMap = {};
    orders.forEach(o => {
      const d = new Date(o.orderDate || o.createdAt);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const lbl = d.toLocaleString('en-IN', { month: 'short', year: '2-digit' });
      if (!monthMap[key]) monthMap[key] = { label: lbl, value: 0, color: '#2563eb' };
      monthMap[key].value += +o.totalAmount || 0;
    });
    const monthBars = Object.entries(monthMap).sort(([a], [b]) => a.localeCompare(b)).slice(-6).map(([, v]) => v);

    /* vendor summary */
    const vendorOrderMap = {};
    orders.forEach(o => {
      const vid = o.vendor?._id || o.vendor;
      if (!vid) return;
      if (!vendorOrderMap[vid]) vendorOrderMap[vid] = { count: 0, total: 0, name: o.vendor?.name || vid };
      vendorOrderMap[vid].count++;
      vendorOrderMap[vid].total += +o.totalAmount || 0;
    });

    return {
      totalProjects: projects.length, byStatus, totalBudget, totalClients: clients.length,
      totalReceived, totalPending, totalPO, receivedPO, pendingPO: totalPO - receivedPO,
      monthBars, vendors, vendorOrderMap, finProjects, projects, orders, clients,
    };
  }, [data]);

  /* ── export CSV ── */
  function exportCSV(rows, filename) {
    if (!rows || !rows.length) return toast.info('No data to export');
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: filename });
    a.click(); URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  }

  function handleExport() {
    if (!metrics) return;
    if (activeReport === 'overview' || activeReport === 'project-summary') {
      exportCSV(metrics.projects.map(p => ({
        ProjectID: p.projectId || '', Title: p.title, Client: p.client?.name || '',
        Status: STATUS_LABEL[p.status] || p.status, Budget: p.budget || 0,
        Start: fmtDate(p.startDate), End: fmtDate(p.endDate),
      })), 'projects.csv');
    } else if (activeReport === 'payment-summary' || activeReport === 'pending-collections') {
      const rows = activeReport === 'pending-collections'
        ? metrics.finProjects.filter(p => p.pending > 0)
        : metrics.finProjects;
      exportCSV(rows.map(p => ({
        Title: p.title, Client: p.client?.name || '', Budget: p.budget || 0,
        Received: p.received || 0, Pending: Math.max(0, p.pending || 0),
        Status: STATUS_LABEL[p.status] || p.status,
      })), activeReport + '.csv');
    } else if (activeReport === 'purchase-summary') {
      exportCSV(metrics.orders.map(o => ({
        PO: o.poNumber || '', Vendor: o.vendor?.name || '', Status: o.status,
        Amount: o.totalAmount || 0, Date: fmtDate(o.orderDate),
      })), 'purchases.csv');
    } else if (activeReport === 'vendor-report') {
      exportCSV(metrics.vendors.map(v => ({
        Name: v.name, Email: v.email || '', Phone: v.phone || '', City: v.city || '',
      })), 'vendors.csv');
    } else if (activeReport === 'customer-report') {
      exportCSV(metrics.clients.map(c => ({
        Name: c.name, Email: c.email || '', Phone: c.phone || '', City: c.city || '',
        Status: c.status || '',
      })), 'customers.csv');
    }
  }

  function clearFilters() {
    setDateRange({ from: '', to: '' });
    setSearch('');
  }

  const hasFilter = dateRange.from || dateRange.to || search;

  if (loading || !metrics) return <Layout><PageLoader text="Loading reports…" /></Layout>;

  const projSlices = Object.entries(metrics.byStatus).map(([s, v]) => ({ color: STATUS_COLOR[s] || '#94a3b8', value: v, label: STATUS_LABEL[s] || s }));
  const paySlices  = [
    { color: '#16a34a', value: metrics.totalReceived, label: 'Received' },
    { color: '#f59e0b', value: metrics.totalPending,  label: 'Pending' },
  ];
  const poSlices = [
    { color: '#16a34a', value: metrics.receivedPO, label: 'Received' },
    { color: '#f59e0b', value: metrics.pendingPO,  label: 'Pending' },
  ];

  return (
    <Layout>
      <div className="page rpt-page">

        {/* ── Header ── */}
        <div className="rpt-header">
          <div className="rpt-header-left">
            <h1 className="rpt-title">Reports</h1>
            <div className="rpt-breadcrumb">
              <span>Home</span><FiChevronRight size={11} /><span>Reports</span>
            </div>
          </div>
          <div className="rpt-header-actions">
            <div className="rpt-date-range">
              <FiCalendar size={13} className="rpt-cal-icon" />
              <input type="date" value={dateRange.from} onChange={e => setDateRange(v => ({ ...v, from: e.target.value }))} />
              <span className="rpt-dash">–</span>
              <input type="date" value={dateRange.to} onChange={e => setDateRange(v => ({ ...v, to: e.target.value }))} />
              {hasFilter && (
                <button className="rpt-clear-filter" onClick={clearFilters} title="Clear filters">
                  <FiX size={12} />
                </button>
              )}
            </div>
            <button className="rpt-btn-ghost" onClick={load}><FiRefreshCw size={13} /> Refresh</button>
            <button className="rpt-btn-primary" onClick={handleExport}><FiDownload size={13} /> Export CSV</button>
          </div>
        </div>

        {/* ── Nav tabs ── */}
        <div className="rpt-tabs">
          {REPORTS.map(r => (
            <button
              key={r.id}
              className={`rpt-tab ${activeReport === r.id ? 'rpt-tab--active' : ''}`}
              onClick={() => setActive(r.id)}
              style={activeReport === r.id ? { borderBottomColor: r.color, color: r.color } : {}}
            >
              <span className="rpt-tab-icon" style={{ color: activeReport === r.id ? r.color : '#94a3b8' }}>{r.icon}</span>
              {r.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {activeReport === 'overview' && (
          <>
            {/* KPI strip */}
            <div className="rpt-kpi-strip">
              {[
                { icon: <FiFolder />, color: '#2563eb', bg: '#eff6ff', value: metrics.totalProjects, label: 'Total Projects' },
                { icon: <FiCheckCircle />, color: '#16a34a', bg: '#f0fdf4', value: metrics.byStatus?.completed || 0, label: 'Completed' },
                { icon: <FiActivity />, color: '#f59e0b', bg: '#fffbeb', value: metrics.byStatus?.in_progress || 0, label: 'In Progress' },
                { icon: <FiUsers />, color: '#0891b2', bg: '#ecfeff', value: metrics.totalClients, label: 'Clients' },
                { icon: <FiTrendingUp />, color: '#16a34a', bg: '#f0fdf4', value: fmt(metrics.totalReceived), label: 'Received' },
                { icon: <FiAlertCircle />, color: '#dc2626', bg: '#fef2f2', value: fmt(metrics.totalPending), label: 'Pending' },
                { icon: <FiShoppingCart />, color: '#7c3aed', bg: '#f5f3ff', value: fmt(metrics.totalPO), label: 'Total Purchases' },
              ].map((m, i) => (
                <div key={i} className="rpt-kpi">
                  <div className="rpt-kpi-icon" style={{ background: m.bg, color: m.color }}>{m.icon}</div>
                  <div className="rpt-kpi-val">{m.value}</div>
                  <div className="rpt-kpi-label">{m.label}</div>
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div className="rpt-charts-row">
              <div className="rpt-chart-card">
                <div className="rpt-chart-header"><FiPieChart size={15} /><span>Project Status</span></div>
                <div className="rpt-chart-body rpt-donut-row">
                  <div className="rpt-donut-wrap">
                    <DonutChart slices={projSlices} size={120} />
                    <div className="rpt-donut-center">
                      <div className="rpt-donut-num">{metrics.totalProjects}</div>
                      <div className="rpt-donut-sub">Total</div>
                    </div>
                  </div>
                  <div className="rpt-legend">
                    {projSlices.map((s, i) => (
                      <div key={i} className="rpt-legend-item">
                        <span className="rpt-legend-dot" style={{ background: s.color }} />
                        <span className="rpt-legend-lbl">{s.label}</span>
                        <span className="rpt-legend-val">{s.value} <small>({pct(s.value, metrics.totalProjects)}%)</small></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rpt-chart-card">
                <div className="rpt-chart-header"><FiDollarSign size={15} /><span>Payment Collection</span></div>
                <div className="rpt-chart-body rpt-donut-row">
                  <div className="rpt-donut-wrap">
                    <DonutChart slices={paySlices} size={120} />
                    <div className="rpt-donut-center">
                      <div className="rpt-donut-num rpt-donut-pct">{pct(metrics.totalReceived, metrics.totalReceived + metrics.totalPending)}%</div>
                      <div className="rpt-donut-sub">Collected</div>
                    </div>
                  </div>
                  <div className="rpt-legend">
                    <div className="rpt-legend-item"><span className="rpt-legend-dot" style={{ background: '#16a34a' }} /><span className="rpt-legend-lbl">Received</span><span className="rpt-legend-val">{fmt(metrics.totalReceived)}</span></div>
                    <div className="rpt-legend-item"><span className="rpt-legend-dot" style={{ background: '#f59e0b' }} /><span className="rpt-legend-lbl">Pending</span><span className="rpt-legend-val">{fmt(metrics.totalPending)}</span></div>
                    <div className="rpt-legend-item rpt-legend-total"><span className="rpt-legend-lbl">Total Budget</span><span className="rpt-legend-val">{fmt(metrics.totalBudget)}</span></div>
                  </div>
                </div>
              </div>

              <div className="rpt-chart-card">
                <div className="rpt-chart-header"><FiShoppingCart size={15} /><span>Purchase Overview</span></div>
                <div className="rpt-chart-body rpt-donut-row">
                  <div className="rpt-donut-wrap">
                    <DonutChart slices={poSlices} size={120} />
                    <div className="rpt-donut-center">
                      <div className="rpt-donut-num rpt-donut-sm">{metrics.orders.length}</div>
                      <div className="rpt-donut-sub">POs</div>
                    </div>
                  </div>
                  <div className="rpt-legend">
                    <div className="rpt-legend-item"><span className="rpt-legend-dot" style={{ background: '#16a34a' }} /><span className="rpt-legend-lbl">Received</span><span className="rpt-legend-val">{fmt(metrics.receivedPO)}</span></div>
                    <div className="rpt-legend-item"><span className="rpt-legend-dot" style={{ background: '#f59e0b' }} /><span className="rpt-legend-lbl">Pending</span><span className="rpt-legend-val">{fmt(metrics.pendingPO)}</span></div>
                    <div className="rpt-legend-item rpt-legend-total"><span className="rpt-legend-lbl">Total</span><span className="rpt-legend-val">{fmt(metrics.totalPO)}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly purchase bar */}
            {metrics.monthBars.length > 0 && (
              <div className="rpt-chart-card">
                <div className="rpt-chart-header"><FiTrendingUp size={15} /><span>Monthly Purchase Value</span></div>
                <div className="rpt-chart-body" style={{ padding: '1rem 1.25rem' }}>
                  <BarChart bars={metrics.monthBars} height={90} />
                </div>
              </div>
            )}
          </>
        )}

        {/* ── PROJECT SUMMARY ── */}
        {activeReport === 'project-summary' && (
          <ReportTable
            title="Project Summary"
            icon={<FiFolder />}
            color="#7c3aed"
            search={search}
            onSearch={setSearch}
            headers={['#', 'Project ID', 'Title', 'Client', 'Status', 'Budget', 'Start Date', 'End Date']}
            rows={metrics.projects
              .filter(p => inRange(p.startDate) && (!search || `${p.title} ${p.projectId} ${p.client?.name}`.toLowerCase().includes(search.toLowerCase())))
              .map((p, i) => [
                i + 1,
                <span className="rpt-pid-badge">{p.projectId || '—'}</span>,
                <b>{p.title}</b>,
                p.client?.name || '—',
                <StatusBadge status={p.status} />,
                fmt(p.budget),
                fmtDate(p.startDate),
                fmtDate(p.endDate),
              ])}
          />
        )}

        {/* ── PAYMENT SUMMARY ── */}
        {activeReport === 'payment-summary' && (
          <ReportTable
            title="Payment Summary"
            icon={<FiDollarSign />}
            color="#16a34a"
            search={search}
            onSearch={setSearch}
            headers={['#', 'Project', 'Client', 'Budget', 'Received', 'Pending', 'Status']}
            rows={metrics.finProjects
              .filter(p => !search || `${p.title} ${p.client?.name}`.toLowerCase().includes(search.toLowerCase()))
              .map((p, i) => [
                i + 1,
                <b>{p.title}</b>,
                p.client?.name || '—',
                fmt(p.budget),
                <span className="rpt-amount-green">{fmt(p.received)}</span>,
                <span className={p.pending > 0 ? 'rpt-amount-red' : 'rpt-amount-green'}>{fmt(Math.max(0, p.pending))}</span>,
                <StatusBadge status={p.status} />,
              ])}
            summary={
              <div className="rpt-summary-strip">
                <div className="rpt-sum-item"><span>Total Budget</span><b>{fmt(metrics.totalBudget)}</b></div>
                <div className="rpt-sum-item rpt-sum-green"><span>Total Received</span><b>{fmt(metrics.totalReceived)}</b></div>
                <div className="rpt-sum-item rpt-sum-red"><span>Total Pending</span><b>{fmt(metrics.totalPending)}</b></div>
                <div className="rpt-sum-item"><span>Collection %</span><b>{pct(metrics.totalReceived, metrics.totalBudget)}%</b></div>
              </div>
            }
          />
        )}

        {/* ── PENDING COLLECTIONS ── */}
        {activeReport === 'pending-collections' && (
          <ReportTable
            title="Pending Collections"
            icon={<FiAlertCircle />}
            color="#f59e0b"
            search={search}
            onSearch={setSearch}
            headers={['#', 'Project', 'Client', 'Budget', 'Received', 'Pending', 'Project Status']}
            rows={metrics.finProjects
              .filter(p => p.pending > 0 && (!search || `${p.title} ${p.client?.name}`.toLowerCase().includes(search.toLowerCase())))
              .sort((a, b) => b.pending - a.pending)
              .map((p, i) => [
                i + 1,
                <b>{p.title}</b>,
                p.client?.name || '—',
                fmt(p.budget),
                <span className="rpt-amount-green">{fmt(p.received)}</span>,
                <b className="rpt-amount-red">{fmt(Math.max(0, p.pending))}</b>,
                <StatusBadge status={p.status} />,
              ])}
            emptyText="No pending collections — all payments up to date!"
          />
        )}

        {/* ── PURCHASE SUMMARY ── */}
        {activeReport === 'purchase-summary' && (
          <ReportTable
            title="Purchase Summary"
            icon={<FiShoppingCart />}
            color="#0891b2"
            search={search}
            onSearch={setSearch}
            headers={['#', 'PO Number', 'Project', 'Vendor', 'Items', 'Status', 'Amount', 'Order Date']}
            rows={metrics.orders
              .filter(o => inRange(o.orderDate) && (!search || `${o.poNumber} ${o.vendor?.name} ${o.project?.title}`.toLowerCase().includes(search.toLowerCase())))
              .map((o, i) => [
                i + 1,
                <span className="rpt-pid-badge">{o.poNumber || '—'}</span>,
                o.project?.title || '—',
                o.vendor?.name || '—',
                o.items?.length || 0,
                <span className="rpt-status-badge" style={{ background: (PO_STATUS_COLOR[o.status] || '#94a3b8') + '18', color: PO_STATUS_COLOR[o.status] || '#94a3b8' }}>{o.status}</span>,
                <b>{fmt(o.totalAmount)}</b>,
                fmtDate(o.orderDate),
              ])}
            summary={
              <div className="rpt-summary-strip">
                <div className="rpt-sum-item"><span>Total POs</span><b>{metrics.orders.length}</b></div>
                <div className="rpt-sum-item"><span>Total Value</span><b>{fmt(metrics.totalPO)}</b></div>
                <div className="rpt-sum-item rpt-sum-green"><span>Received</span><b>{fmt(metrics.receivedPO)}</b></div>
                <div className="rpt-sum-item rpt-sum-red"><span>Pending</span><b>{fmt(metrics.pendingPO)}</b></div>
              </div>
            }
          />
        )}

        {/* ── VENDOR REPORT ── */}
        {activeReport === 'vendor-report' && (
          <ReportTable
            title="Vendor Report"
            icon={<FiUsers />}
            color="#dc2626"
            search={search}
            onSearch={setSearch}
            headers={['#', 'Vendor Name', 'Email', 'Phone', 'City', 'Total POs', 'Total Value']}
            rows={metrics.vendors
              .filter(v => !search || `${v.name} ${v.email} ${v.city}`.toLowerCase().includes(search.toLowerCase()))
              .map((v, i) => {
                const summary = metrics.vendorOrderMap[v._id] || { count: 0, total: 0 };
                return [
                  i + 1,
                  <b>{v.name}</b>,
                  v.email || '—',
                  v.phone || '—',
                  v.city || '—',
                  summary.count,
                  <b>{fmt(summary.total)}</b>,
                ];
              })}
            emptyText="No vendors found"
          />
        )}

        {/* ── CUSTOMER REPORT ── */}
        {activeReport === 'customer-report' && (
          <ReportTable
            title="Customer Report"
            icon={<FiUsers />}
            color="#059669"
            search={search}
            onSearch={setSearch}
            headers={['#', 'Name', 'Email', 'Phone', 'City', 'Projects', 'Status']}
            rows={metrics.clients
              .filter(c => !search || `${c.name} ${c.email} ${c.city}`.toLowerCase().includes(search.toLowerCase()))
              .map((c, i) => {
                const projCount = metrics.projects.filter(p => p.client?._id === c._id || p.client === c._id).length;
                return [
                  i + 1,
                  <b>{c.name}</b>,
                  c.email || '—',
                  c.phone || '—',
                  c.city || '—',
                  projCount,
                  <span className="rpt-status-badge" style={{ background: c.status === 'active' ? '#f0fdf4' : '#f8fafc', color: c.status === 'active' ? '#16a34a' : '#94a3b8' }}>{c.status || 'active'}</span>,
                ];
              })}
            emptyText="No clients found"
          />
        )}

      </div>
    </Layout>
  );
}

/* ── Status badge ── */
function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || '#94a3b8';
  return (
    <span className="rpt-status-badge" style={{ background: color + '18', color }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

/* ── Reusable table component ── */
function ReportTable({ title, icon, color, headers, rows, summary, emptyText = 'No data found', search, onSearch }) {
  const [page, setPage] = useState(1);
  const PAGE = 15;
  const total = Math.max(1, Math.ceil(rows.length / PAGE));
  const paged = rows.slice((page - 1) * PAGE, page * PAGE);

  useEffect(() => { setPage(1); }, [rows.length, search]);

  return (
    <div className="rpt-report-panel">
      <div className="rpt-rp-header" style={{ borderLeftColor: color }}>
        <div className="rpt-rp-title-row">
          <span className="rpt-rp-icon" style={{ color }}>{icon}</span>
          <h3 className="rpt-rp-title">{title}</h3>
          <span className="rpt-rp-count">{rows.length} records</span>
        </div>
        <div className="rpt-rp-search-wrap">
          <input
            className="rpt-rp-search"
            placeholder="Search…"
            value={search}
            onChange={e => onSearch(e.target.value)}
          />
          {search && <button className="rpt-rp-clear" onClick={() => onSearch('')}><FiX size={12} /></button>}
        </div>
      </div>

      {summary && summary}

      <div className="rpt-table-wrap">
        <table className="rpt-data-table">
          <thead>
            <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {paged.length === 0 && (
              <tr><td colSpan={headers.length} className="rpt-empty">{emptyText}</td></tr>
            )}
            {paged.map((row, ri) => (
              <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > 1 && (
        <div className="rpt-pagination">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
          {Array.from({ length: total }, (_, i) => i + 1)
            .filter(p => p === 1 || p === total || Math.abs(p - page) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
              acc.push(p); return acc;
            }, [])
            .map((p, i) => typeof p === 'string'
              ? <span key={i} className="rpt-pg-ellipsis">…</span>
              : <button key={i} className={page === p ? 'rpt-pg-active' : ''} onClick={() => setPage(p)}>{p}</button>
            )}
          <button disabled={page === total} onClick={() => setPage(p => p + 1)}>Next ›</button>
        </div>
      )}
    </div>
  );
}
